/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { useIntl } from 'react-intl';

import { Radio, RadioGroup } from '@cimpress/react-components';

import usePackCalculator from '../../hooks/usePackCalculator';
import { BIGGEST_PACKS, EVEN_PACKS } from '../../reducers/editConstants';
import Loading from '../Loading';
import ErrorDisplay from '../shared/ErrorDisplay';
import FormulaDisplay from './FormulaDisplay';
import messages from './messages';

type PackCalculatorDisplayProps = {
  packCalculatorId: string;
  mappingsLoaded: Set<String>;
  setMappingsLoaded: (mappingsLoaded: Set<String>) => void;
  showCalculatorNameAsSubheading?: boolean;
  showPackFormulasHeading?: boolean;
  useLegacySingleCalculatorLayout?: boolean;
  productInfoContent?: React.ReactNode;
};

/**
 * Component to display a single pack calculator's formulas and configuration.
 * This component fetches and displays pack calculator data including formulas,
 * shredding strategy, quantities, and dimensions.
 */
const PackCalculatorDisplay = ({
  packCalculatorId,
  mappingsLoaded,
  setMappingsLoaded,
  showCalculatorNameAsSubheading = false,
  showPackFormulasHeading = true,
  useLegacySingleCalculatorLayout = false,
  productInfoContent,
}: PackCalculatorDisplayProps) => {
  const { formatMessage } = useIntl();

  const packCalculatorQuery = usePackCalculator({
    id: packCalculatorId,
    onSuccess: () => {
      const newSet: Set<String> = new Set(mappingsLoaded);
      newSet.add(packCalculatorId || '');
      setMappingsLoaded(newSet);
    },
  });

  if (packCalculatorQuery.isError) {
    return (
      <div style={{ margin: 'auto', marginTop: '30px' }}>
        <ErrorDisplay errorMsg={formatMessage(messages.errorMessage)} />
      </div>
    );
  }

  if (packCalculatorQuery.isLoading || packCalculatorQuery.isIdle) {
    return <Loading />;
  }

  const packCalculatorData = packCalculatorQuery.data;

  const packBodyStyle = css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 20px;
  `;

  // Formula content that will be rendered in left column
  const formulaContent = (
    <>
      {showPackFormulasHeading && <h4>{formatMessage(messages.packFormulas)}</h4>}
      
      {showCalculatorNameAsSubheading && (
        <h5 style={{ marginTop: '15px', marginBottom: '10px', fontWeight: 'bold' }}>
          {packCalculatorData.name}
        </h5>
      )}

      <RadioGroup
        inline
        name={`shreddingStrategy-${packCalculatorId}`}
        valueSelected={
          packCalculatorData.shreddingStrategy ??
          (packCalculatorData.packableQuantities ? BIGGEST_PACKS : EVEN_PACKS)
        }
      >
        <Radio label={formatMessage(messages.useBiggestPacks)} value={BIGGEST_PACKS} disabled />
        <Radio label={formatMessage(messages.dividePacksEvenly)} value={EVEN_PACKS} disabled />
      </RadioGroup>

      {Boolean(packCalculatorData.packableQuantities) && (
        <FormulaDisplay
          label={formatMessage(messages.quantities)}
          value={packCalculatorData.packableQuantities?.join(', ')}
        />
      )}

      {Boolean(packCalculatorData.maxQuantity) && (
        <FormulaDisplay
          label={formatMessage(messages.maxQuantity)}
          value={String(packCalculatorData.maxQuantity)}
        />
      )}

      <FormulaDisplay
        label={formatMessage(messages.preferredPackQuantity)}
        value={String(packCalculatorData.preferredPackQuantity ?? '')}
      />

      <FormulaDisplay
        label={formatMessage(messages.packsPerContainer)}
        value={packCalculatorData.packsPerContainer}
      />

      <FormulaDisplay
        label={formatMessage(messages.weightFormula)}
        value={packCalculatorData.weight?.value}
        unit={packCalculatorData.weight?.unit}
      />

      <FormulaDisplay
        label={formatMessage(messages.preferredContainerType)}
        value={packCalculatorData.preferredContainerType}
      />

      <FormulaDisplay
        label={formatMessage(messages.lengthFormula)}
        value={packCalculatorData.length?.value}
        unit={packCalculatorData.length?.unit}
      />

      <FormulaDisplay
        label={formatMessage(messages.widthFormula)}
        value={packCalculatorData.width?.value}
        unit={packCalculatorData.width?.unit}
      />

      <FormulaDisplay
        label={formatMessage(messages.heightFormula)}
        value={packCalculatorData.height?.value}
        unit={packCalculatorData.height?.unit}
      />

      <FormulaDisplay
        label={formatMessage(messages.diameterFormula)}
        value={packCalculatorData.diameter?.value}
        unit={packCalculatorData.diameter?.unit}
      />

      <FormulaDisplay
        label={formatMessage(messages.volumeFormula)}
        value={packCalculatorData.volume?.value}
        unit={packCalculatorData.volume?.unit}
      />
    </>
  );

  // Legacy single calculator layout: Show calculator name as main heading with grid
  if (useLegacySingleCalculatorLayout && productInfoContent) {
    return (
      <>
        <h2 style={{ paddingBottom: 10 }}>{packCalculatorData.name}</h2>
        <div css={packBodyStyle}>
          <div>{formulaContent}</div>
          <div>{productInfoContent}</div>
        </div>
      </>
    );
  }

  // New multi-calculator layout: Just return formula content (parent handles grid)
  return <div style={{ marginBottom: showCalculatorNameAsSubheading ? '30px' : '0' }}>{formulaContent}</div>;
};

export default PackCalculatorDisplay;
